/**
 * Técnica contable: política de medición de activos digitales.
 *
 * Una sola fuente en Markdown, tres presentaciones: esta pantalla, la hoja del
 * XLSX y la sección del PDF. Mantener el texto en tres sitios sería garantizar
 * que dejen de decir lo mismo.
 *
 * El Markdown se renderiza con un formateador mínimo y propio: encabezados,
 * tablas, listas y bloques de código es todo lo que el documento usa, y añadir
 * una dependencia para eso ampliaría la superficie de la aplicación a cambio de
 * nada. No se interpreta HTML embebido — el contenido se escapa siempre.
 */

import { useClosing } from "./closingContext";
import { StatementHead } from "./shared";

export function AccountingPolicy() {
  const { package: pkg } = useClosing();
  return (
    <>
      <StatementHead
        title="Técnica Contable"
        subtitle="política de medición de activos digitales"
        homologation={pkg.homologation.accounting_policy}
      />
      <div className="card">
        {renderMarkdown(pkg.policy)}
      </div>
    </>
  );
}

type Block =
  | { kind: "heading"; level: number; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "list"; items: string[] }
  | { kind: "code"; text: string }
  | { kind: "table"; header: string[]; rows: string[][] }
  | { kind: "quote"; text: string };

/** Parte el documento en bloques. Sin `dangerouslySetInnerHTML` en ningún punto. */
function parseMarkdown(source: string): Block[] {
  const blocks: Block[] = [];
  const lines = source.split("\n");
  let index = 0;

  const flushParagraph = (buffer: string[]) => {
    if (buffer.length) blocks.push({ kind: "paragraph", text: buffer.join(" ") });
  };

  let paragraph: string[] = [];
  while (index < lines.length) {
    const line = lines[index];

    if (line.startsWith("```")) {
      flushParagraph(paragraph);
      paragraph = [];
      const code: string[] = [];
      index += 1;
      while (index < lines.length && !lines[index].startsWith("```")) {
        code.push(lines[index]);
        index += 1;
      }
      blocks.push({ kind: "code", text: code.join("\n") });
      index += 1;
      continue;
    }

    if (line.startsWith("|")) {
      flushParagraph(paragraph);
      paragraph = [];
      const filas: string[][] = [];
      while (index < lines.length && lines[index].startsWith("|")) {
        const celdas = lines[index]
          .split("|")
          .slice(1, -1)
          .map((cell) => cell.trim());
        // La fila de guiones solo separa el encabezado; no es contenido.
        if (!celdas.every((cell) => /^:?-{2,}:?$/.test(cell))) filas.push(celdas);
        index += 1;
      }
      if (filas.length) {
        blocks.push({ kind: "table", header: filas[0], rows: filas.slice(1) });
      }
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      flushParagraph(paragraph);
      paragraph = [];
      blocks.push({ kind: "heading", level: heading[1].length, text: heading[2] });
      index += 1;
      continue;
    }

    if (line.startsWith("> ")) {
      flushParagraph(paragraph);
      paragraph = [];
      const quote: string[] = [];
      while (index < lines.length && lines[index].startsWith(">")) {
        quote.push(lines[index].replace(/^>\s?/, ""));
        index += 1;
      }
      blocks.push({ kind: "quote", text: quote.join(" ") });
      continue;
    }

    if (/^[-*]\s+/.test(line)) {
      flushParagraph(paragraph);
      paragraph = [];
      const items: string[] = [];
      while (index < lines.length && /^[-*]\s+/.test(lines[index])) {
        items.push(lines[index].replace(/^[-*]\s+/, ""));
        index += 1;
      }
      blocks.push({ kind: "list", items });
      continue;
    }

    if (line.trim() === "") {
      flushParagraph(paragraph);
      paragraph = [];
      index += 1;
      continue;
    }

    paragraph.push(line.trim());
    index += 1;
  }
  flushParagraph(paragraph);
  return blocks;
}

/** Quita el énfasis de Markdown; el texto se inserta como texto, nunca como HTML. */
function plain(text: string): string {
  return text.replace(/\*\*(.+?)\*\*/g, "$1").replace(/`(.+?)`/g, "$1");
}

function renderMarkdown(source: string) {
  if (!source.trim()) {
    return <p className="muted">El documento de técnica contable no está disponible.</p>;
  }
  return (
    <div className="policy">
      {parseMarkdown(source).map((block, index) => {
        switch (block.kind) {
          case "heading": {
            const Tag = (block.level <= 2 ? "h2" : "h3") as "h2" | "h3";
            return <Tag key={index}>{plain(block.text)}</Tag>;
          }
          case "list":
            return (
              <ul key={index}>
                {block.items.map((item, position) => (
                  <li key={position}>{plain(item)}</li>
                ))}
              </ul>
            );
          case "code":
            return (
              <pre key={index} className="mono">
                {block.text}
              </pre>
            );
          case "quote":
            return (
              <p key={index} className="small muted">
                {plain(block.text)}
              </p>
            );
          case "table":
            return (
              <div className="table-wrap" key={index}>
                <table>
                  <thead>
                    <tr>
                      {block.header.map((cell, position) => (
                        <th key={position} scope="col">
                          {plain(cell)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, position) => (
                      <tr key={position}>
                        {row.map((cell, cellPosition) => (
                          <td key={cellPosition}>{plain(cell)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          default:
            return <p key={index}>{plain(block.text)}</p>;
        }
      })}
    </div>
  );
}
